import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { AnimatedSection } from './AnimatedSection';
import { trackCalendlyConversion } from './GoogleTagLoader';
export function CaseStudiesSection() {
  const caseStudies = [{
    agency: 'Northbound Studio',
    headline: 'Added events as a new go-to-market channel',
    description: 'This six-person software studio wanted to offer events to clients but could not justify an events team. Turnout became their events team, handling identity, outreach, and venues so they could launch a full event practice overnight.',
    metrics: [{
      value: '4',
      label: 'Events Launched'
    }, {
      value: '6',
      label: 'New Clients Won'
    }],
    link: 'https://www.phoxevents.com/about',
    initial: 'N'
  }, {
    agency: 'Tinroof Collective',
    headline: 'Delivered full-service events without a single new hire',
    description: 'A boutique marketing agency needed to run flagship events for clients without growing the team. Turnout handled everything from event identity to co-hosting on the day, turning events into their highest-converting pipeline source.',
    metrics: [{
      value: '0',
      label: 'New Hires'
    }, {
      value: '3x',
      label: 'Pipeline From Events'
    }],
    link: 'https://buflusevents.com/',
    initial: 'T'
  }, {
    agency: 'Brightwork Agency',
    headline: 'Co-hosts 10 client events a year with a lean team',
    description: 'Scaling a recurring event program across multiple clients used to be impossible for a small agency. Turnout manages venues, guest outreach, and host prep, and co-hosts on the day, so Brightwork delivers like a full events shop.',
    metrics: [{
      value: '10',
      label: 'Events/Year'
    }, {
      value: '100%',
      label: 'Client Retention'
    }],
    link: 'https://ondotexperience.com/',
    initial: 'B'
  }];
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };
  return <section id="case-studies" className="py-24 bg-gray-50" data-id="element-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-41">
        <AnimatedSection data-id="element-42">
          <div className="text-center mb-16" data-id="element-43">
            <p className="text-sm font-semibold text-indigo-600 tracking-wide uppercase mb-3" data-id="element-44">
              Real Results
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl" data-id="element-45">
              Agencies That Added Events With Turnout
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto" data-id="element-46">
              Lean teams that launched full event practices without hiring.
            </p>
          </div>
        </AnimatedSection>

        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} data-id="element-47">
          {caseStudies.map((study, index) => <motion.a key={index} href={study.link} target="_blank" rel="noopener noreferrer" variants={cardVariants} whileHover={{
          y: -4
        }} className="group relative bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col h-full cursor-pointer" data-id="element-48">
              {/* Top accent line */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-indigo-600 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" data-id="element-49" />

              <div className="p-8 flex-1 flex flex-col relative z-10" data-id="element-50">
                {/* Header: Logo + Brand Name */}
                <div className="mb-6" data-id="element-51">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest" data-id="element-52">
                    {study.agency}
                  </span>
                </div>

                {/* Narrative Headline */}
                <h3 className="text-xl font-bold text-gray-900 mb-6 leading-tight group-hover:text-indigo-600 transition-colors duration-200" data-id="element-53">
                  {study.headline}
                </h3>

                {/* Metrics Row */}
                <div className="grid grid-cols-2 gap-4 mb-6 pt-6 border-t border-gray-100 relative" data-id="element-54">
                  {/* Watermark behind metrics */}
                  <div className="absolute -right-2 -bottom-4 text-9xl font-black text-gray-50 opacity-[0.03] select-none pointer-events-none font-serif leading-none z-0" data-id="element-55">
                    {study.initial}
                  </div>

                  {study.metrics.map((metric, i) => <div key={i} className="relative z-10" data-id="element-56">
                      <p className="text-3xl font-bold text-indigo-600 tracking-tight" data-id="element-57">
                        {metric.value}
                      </p>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mt-1" data-id="element-58">
                        {metric.label}
                      </p>
                    </div>)}
                </div>

                {/* Description */}
                <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-1" data-id="element-59">
                  {study.description}
                </p>

                {/* Read More Link */}
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:gap-2 transition-all duration-200 mt-auto" data-id="element-60">
                  <span data-id="element-61">Read full story</span>
                  <ArrowUpRight className="h-4 w-4 ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" data-id="element-62" />
                </div>
              </div>
            </motion.a>)}
        </motion.div>

        <AnimatedSection delay={0.5} data-id="element-63">
          <div className="mt-16 text-center" data-id="element-64">
            <a href="https://calendly.com/dan-at-turnout/turnout-general-30-min-call" target="_blank" rel="noopener noreferrer" onClick={trackCalendlyConversion} className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-xl shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 transition-colors duration-300" data-id="element-65">
              See How It Works
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>;
}